import api, { getCommonHeaders } from './api';
import type {
  Dashboard,
  DashboardBlock,
  DashboardWithBlocks,
  DashboardStatus,
  BlockStatus,
  Position,
  ControlValues,
  DashboardConversationHistoryResponse,
} from '../types/dashboard';
import type { V2StreamEvent } from '@/types/chat';

export type DashboardSSEHandler = (event: V2StreamEvent) => void;

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.zamino.com';

interface CreateDashboardRequest {
  name: string;
  description?: string;
  data_mode?: 'static' | 'live';
  theme?: 'dark' | 'light';
  layout_columns?: number;
}

interface UpdateDashboardRequest {
  name?: string;
  description?: string | null;
  data_mode?: 'static' | 'live';
  theme?: 'dark' | 'light';
  layout_columns?: number;
  status?: DashboardStatus;
}

interface ListDashboardsParams {
  status?: DashboardStatus;
}

interface BlockDataResult {
  block_id: string;
  success: boolean;
  data?: Record<string, unknown>[];
  error?: string;
}

export const dashboardApi = {
  create: async (
    data: CreateDashboardRequest
  ): Promise<{ success: boolean; data: Dashboard }> => {
    const response = await api.post('/api/dashboards', data);
    return response.data;
  },

  list: async (
    params: ListDashboardsParams = {}
  ): Promise<{ success: boolean; data: Dashboard[] }> => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    const qs = queryParams.toString();
    const url = qs ? `/api/dashboards?${qs}` : '/api/dashboards';
    const response = await api.get(url);
    return response.data;
  },

  getById: async (
    id: string
  ): Promise<{ success: boolean; data: DashboardWithBlocks }> => {
    const response = await api.get(`/api/dashboards/${id}`);
    return response.data;
  },

  update: async (
    id: string,
    changes: UpdateDashboardRequest
  ): Promise<{ success: boolean; data: Dashboard }> => {
    const response = await api.patch(`/api/dashboards/${id}`, changes);
    return response.data;
  },

  remove: async (
    id: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/api/dashboards/${id}`);
    return response.data;
  },

  /**
   * Send a message via the V2 agent runtime (SSE stream).
   * Resolves when the stream ends; rejects on HTTP or network errors.
   */
  sendMessageStream: async (
    dashboardId: string,
    message: string,
    onEvent: DashboardSSEHandler,
    metadata?: { type: string; block_id: string; block_title: string },
    signal?: AbortSignal,
  ): Promise<void> => {
    const response = await fetch(
      `${API_BASE_URL}/api/v2/dashboards/${dashboardId}/conversation`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getCommonHeaders()),
        },
        body: JSON.stringify({ message, metadata }),
        signal,
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(errorData.error || errorData.details || `HTTP ${response.status}`);
    }

    if (!response.body) {
      throw new Error('No response body for SSE stream');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const parsed = JSON.parse(line.slice(6)) as V2StreamEvent;
              onEvent(parsed);
            } catch {
              // Skip malformed JSON
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  },

  updateBlockStatus: async (
    dashboardId: string,
    blockId: string,
    status: BlockStatus
  ): Promise<{ success: boolean; data: DashboardBlock }> => {
    const response = await api.patch(
      `/api/dashboards/${dashboardId}/blocks/${blockId}/status`,
      { status }
    );
    return response.data;
  },

  getConversation: async (
    dashboardId: string
  ): Promise<DashboardConversationHistoryResponse> => {
    const response = await api.get(
      `/api/v2/dashboards/${dashboardId}/conversation`
    );
    return response.data;
  },

  fetchAllData: async (
    dashboardId: string,
    controlValues?: ControlValues
  ): Promise<{ success: boolean; data: BlockDataResult[] }> => {
    let url = `/api/dashboards/${dashboardId}/data`;
    if (controlValues) {
      const encoded = encodeURIComponent(JSON.stringify(controlValues));
      url += `?controlValues=${encoded}`;
    }
    const response = await api.get(url);
    return response.data;
  },

  fetchBlockData: async (
    dashboardId: string,
    blockId: string,
    controlValues?: ControlValues
  ): Promise<{ success: boolean; data: Record<string, unknown>[] }> => {
    let url = `/api/dashboards/${dashboardId}/data/${blockId}`;
    if (controlValues) {
      const encoded = encodeURIComponent(JSON.stringify(controlValues));
      url += `?controlValues=${encoded}`;
    }
    const response = await api.get(url);
    return response.data;
  },

  updateBlockPositions: async (
    dashboardId: string,
    positions: { block_id: string; position: Position }[]
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.patch(
      `/api/dashboards/${dashboardId}/blocks/positions`,
      { positions }
    );
    return response.data;
  },
};

export default dashboardApi;
